import { create } from 'zustand';
import { ophimSyncService } from '@/services/ophimSyncService';
import { SyncStatus, SyncLog } from '@/types/kkmovie';

interface OPhimSyncState {
  syncStatus: SyncStatus | null;
  syncHistory: SyncLog[];
  historyPagination: { page: number; limit: number; total: number; totalPages: number };
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;

  // Actions
  fetchSyncStatus: () => Promise<void>;
  fetchSyncHistory: (page?: number, limit?: number) => Promise<void>;
  triggerSync: () => Promise<boolean>;
  syncSingleMovie: (slug: string) => Promise<boolean>;
}

export const useOPhimSyncStore = create<OPhimSyncState>()((set, get) => ({
  syncStatus: null,
  syncHistory: [],
  historyPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  isLoading: false,
  isSyncing: false,
  error: null,

  fetchSyncStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await ophimSyncService.getSyncStatus();
      if (res.ok && res.data) {
        const responseData = res.data as any;
        const data = responseData?.data?.data || responseData?.data || responseData;
        set({ syncStatus: data as SyncStatus, isLoading: false });
      } else {
        set({ isLoading: false, error: (res as any)?.data?.message || 'Lỗi khi tải trạng thái sync' });
      }
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Lỗi khi tải trạng thái sync' });
    }
  },

  fetchSyncHistory: async (page = 1, limit = 20) => {
    set({ isLoading: true, error: null });
    try {
      const res = await ophimSyncService.getSyncHistory(page, limit);
      if (res.ok && res.data) {
        const data: any = res.data.data;
        set({
          syncHistory: data?.data || [],
          historyPagination: data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
          isLoading: false,
        });
      } else {
        set({ isLoading: false, error: (res as any)?.data?.message || 'Lỗi khi tải lịch sử sync' });
      }
    } catch (e) {
      set({ isLoading: false, error: e instanceof Error ? e.message : 'Lỗi khi tải lịch sử sync' });
    }
  },

  triggerSync: async () => {
    set({ isSyncing: true, error: null });
    try {
      const res = await ophimSyncService.triggerSync();
      if (res.ok) {
        await get().fetchSyncStatus();
        set({ isSyncing: false });
        return true;
      }
      set({ isSyncing: false, error: (res as any)?.data?.message || 'Lỗi khi trigger sync' });
      return false;
    } catch (e) {
      set({ isSyncing: false, error: e instanceof Error ? e.message : 'Lỗi khi trigger sync' });
      return false;
    }
  },

  syncSingleMovie: async (slug) => {
    set({ isSyncing: true, error: null });
    try {
      const res = await ophimSyncService.syncSingleMovie(slug);
      if (res.ok) {
        await get().fetchSyncStatus();
        set({ isSyncing: false });
        return true;
      }
      set({ isSyncing: false, error: (res as any)?.data?.message || 'Lỗi khi sync phim' });
      return false;
    } catch (e) {
      set({ isSyncing: false, error: e instanceof Error ? e.message : 'Lỗi khi sync phim' });
      return false;
    }
  },
}));
